import type { HealthQuestionnaireAnswerItemInput } from 'src/common/api/apollo/additional';
import { EMPTY_ANSWERS, type AnyQuestion, type GqlQuestion, type QuestionnaireAnswers } from './healthQuestionnaire.types';

export function collectFollowUpParentMap(questions: AnyQuestion[]): Record<string, string> {
  const map: Record<string, string> = {};

  const walk = (qs: AnyQuestion[]) => {
    for (const q of qs) {
      if ('followUp' in q && q.followUp?.code) {
        map[q.followUp.code] = q.code;
      }
      const children = 'questions' in q ? (q as GqlQuestion).questions : undefined;
      if (children?.length) walk(children as AnyQuestion[]);
    }
  };

  walk(questions);
  return map;
}

function serializeQuestionValue(v: boolean | null | undefined): string {
  if (v == null) return '';
  return v ? 'true' : 'false';
}

function mergeIntoMap(
  map: Map<string, HealthQuestionnaireAnswerItemInput>,
  input: HealthQuestionnaireAnswerItemInput,
) {
  const existing = map.get(input.questionCode);
  if (!existing) {
    map.set(input.questionCode, {
      ...input,
      followUpAnswers: input.followUpAnswers ? [...input.followUpAnswers] : undefined,
    });
    return;
  }

  const fieldMap = new Map<string, string>();
  for (const x of existing.followUpAnswers ?? []) fieldMap.set(x.fieldCode, x.value);
  for (const x of input.followUpAnswers ?? []) fieldMap.set(x.fieldCode, x.value);

  map.set(input.questionCode, {
    questionCode: input.questionCode,
    value: input.value !== '' ? input.value : existing.value,
    followUpAnswers: fieldMap.size > 0
      ? Array.from(fieldMap, ([fieldCode, value]) => ({ fieldCode, value }))
      : existing.followUpAnswers,
  });
}

/**
 * Build `answers` array items for the batch save mutation (deltas between `prev` and `next`).
 */
export function buildSaveInputsFromAnswerDelta(
  prev: QuestionnaireAnswers,
  next: QuestionnaireAnswers,
  followUpParentMap: Record<string, string>,
): HealthQuestionnaireAnswerItemInput[] {
  const map = new Map<string, HealthQuestionnaireAnswerItemInput>();

  const qKeys = Array.from(
    new Set([...Object.keys(prev.questions), ...Object.keys(next.questions)]),
  );
  for (const code of qKeys) {
    if (prev.questions[code] !== next.questions[code]) {
      mergeIntoMap(map, {
        questionCode: code,
        value: serializeQuestionValue(next.questions[code]),
      });
    }
  }

  const cbKeys = Array.from(
    new Set([...Object.keys(prev.checkboxes), ...Object.keys(next.checkboxes)]),
  );
  for (const code of cbKeys) {
    const prevS = JSON.stringify(prev.checkboxes[code] ?? []);
    const nextS = JSON.stringify(next.checkboxes[code] ?? []);
    if (prevS !== nextS) {
      mergeIntoMap(map, {
        questionCode: code,
        value: nextS,
      });
    }
  }

  const tKeys = Array.from(
    new Set([...Object.keys(prev.textInputs), ...Object.keys(next.textInputs)]),
  );
  for (const code of tKeys) {
    if (prev.textInputs[code] !== next.textInputs[code]) {
      mergeIntoMap(map, {
        questionCode: code,
        value: next.textInputs[code] ?? '',
      });
    }
  }

  const tblKeys = Array.from(
    new Set([...Object.keys(prev.tables ?? {}), ...Object.keys(next.tables ?? {})]),
  );
  for (const code of tblKeys) {
    const prevS = JSON.stringify(prev.tables?.[code] ?? {});
    const nextS = JSON.stringify(next.tables?.[code] ?? {});
    if (prevS !== nextS) {
      mergeIntoMap(map, {
        questionCode: code,
        value: nextS,
      });
    }
  }

  const fuKeys = Array.from(
    new Set([...Object.keys(prev.followUpFields), ...Object.keys(next.followUpFields)]),
  );
  for (const fuCode of fuKeys) {
    const prevS = JSON.stringify(prev.followUpFields[fuCode] ?? {});
    const nextS = JSON.stringify(next.followUpFields[fuCode] ?? {});
    if (prevS !== nextS) {
      const parentCode = followUpParentMap[fuCode];
      if (parentCode) {
        const fields = next.followUpFields[fuCode] ?? {};
        const followUpAnswers = Object.entries(fields).map(([fieldCode, value]) => ({ fieldCode, value }));

        mergeIntoMap(map, {
          questionCode: parentCode,
          value: serializeQuestionValue(next.questions[parentCode]),
          followUpAnswers: followUpAnswers.length ? followUpAnswers : undefined,
        });
      }
    }
  }

  return Array.from(map.values());
}

/**
 * Build `answers` for a full (re)submit: a snapshot of every currently answered
 * question, plus explicit empty values for answers that existed in `initial` but
 * were cleared — a delta from EMPTY_ANSWERS alone skips those (cleared table `{}`
 * equals the empty default), so the backend would keep the stale saved answer.
 */
export function buildSubmitAnswerInputs(
  initial: Partial<QuestionnaireAnswers>,
  current: QuestionnaireAnswers,
  followUpParentMap: Record<string, string>,
): HealthQuestionnaireAnswerItemInput[] {
  const snapshot = buildSaveInputsFromAnswerDelta(EMPTY_ANSWERS, current, followUpParentMap);
  const changesWithClears = buildSaveInputsFromAnswerDelta(
    { ...EMPTY_ANSWERS, ...initial },
    current,
    followUpParentMap,
  );

  const map = new Map<string, HealthQuestionnaireAnswerItemInput>();
  for (const item of snapshot) map.set(item.questionCode, item);
  for (const item of changesWithClears) {
    const existing = map.get(item.questionCode);
    map.set(item.questionCode, {
      ...item,
      followUpAnswers: item.followUpAnswers ?? existing?.followUpAnswers,
    });
  }
  return Array.from(map.values());
}
